import { Delete, Edit } from '@mui/icons-material';
import { ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import moment from 'moment';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { ListWidgetComponentFactory } from '~/components/MyWorkspace/ListWidget';
import { AbsenceEntry } from '~/data/absences/types';
import { DeleteAbsenceDialog } from '~/features/AbsenceChanges/DeleteAbsenceDialog';
import { EditAbsenceDialog } from '~/features/AbsenceChanges/EditAbsenceDialog';

import { WidgetProps } from '../../data/widgets';

import { Absence } from './components/Absence';
import { AbsenceInfo } from './components/AbsenceInfo';
import { useMyAbsences } from './useMyAbsences';

export interface Props extends WidgetProps<{ disableAbsenceLinks?: boolean }> {}

export const getEmployeeAbsencesReqParams = {
  employeeId: 'me',
  startDate: moment().startOf('day').toDate(),
  endDate: moment().add(3, 'month').startOf('day').toDate(),
};

export const MyAbsencesWidget: React.FC<Props> = ({ title, config }) => {
  const { t } = useTranslation();

  const {
    employeeId,
    isLoading,
    failedToLoad,
    permissions: { canAddAbsence, canEditSelectedAbsence },
    absences,
    dialogStates: {
      edit: [editDialogState, setEditDialogState],
      delete: [deleteAbsenceId, setDeleteAbsenceId],
    },
  } = useMyAbsences();

  const MenuActions = (props: { absenceEntry: AbsenceEntry }) => {
    const {
      absenceEntry: { uid },
    } = props;

    const canEdit = !!uid && canEditSelectedAbsence(uid);

    return !!uid && canEdit ? (
      <>
        <MenuItem
          onClick={() =>
            setEditDialogState({
              open: true,
              absenceId: uid,
            })
          }
          disabled={!canEdit}
        >
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('edit')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => setDeleteAbsenceId(uid)} disabled={!canEdit}>
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('delete')}</ListItemText>
        </MenuItem>
      </>
    ) : null;
  };

  const MyAbsencesWidgetComponent = ListWidgetComponentFactory({
    ListItemComponent: (listItemProps) => {
      const { listItem } = listItemProps;

      return (
        <Absence
          {...listItemProps}
          listItemMenuContent={
            !!listItem.uid && canEditSelectedAbsence(listItem.uid) ? (
              <MenuActions absenceEntry={listItem} />
            ) : undefined
          }
          isLinkEnabled={!config?.disableAbsenceLinks}
        />
      );
    },
    getIdentifier: (item: AbsenceEntry) =>
      item?.uid ??
      `${
        item.reason?.code
      }${item.startDate?.toString()}${item.endDate?.toString()}`,
    ListItemInfoComponent: AbsenceInfo,
    dataCy: 'my-absences-widget',
  });

  return (
    <>
      <MyAbsencesWidgetComponent
        title={title || t('myAbsences')}
        data={absences}
        loading={isLoading}
        failedToLoad={failedToLoad}
        noDataMessage={t('noAbsences')}
        onAddListItemClick={() => setEditDialogState({ open: true })}
        isTabDisabled={!canAddAbsence}
      />
      <EditAbsenceDialog
        employeeId={employeeId}
        onClose={() => setEditDialogState({ open: false })}
        {...editDialogState}
      />
      <DeleteAbsenceDialog
        employeeId={employeeId}
        onClose={() => setDeleteAbsenceId(undefined)}
        open={!!deleteAbsenceId}
        absenceId={deleteAbsenceId}
      />
    </>
  );
};
